/**
 * Admin wallet API client — F-012 Phase 3 (IMPL-P3-002).
 *
 * Typed `fetch` wrappers for the admin fido endpoints (Phase 2):
 *  - GET   /api/v1/admin/clienti                  → ClienteListResponse
 *  - GET   /api/v1/admin/clienti/:id              → ClienteDetailView
 *  - PATCH /api/v1/admin/clienti/:id/fido         → FidoMutationResponse
 *  - PATCH /api/v1/admin/clienti/:id/wallet-toggle→ FidoMutationResponse
 *
 * Conventions mirror `features/archivio/lib/archivio-client.ts`:
 *  - `credentials: 'include'` so the Better Auth session cookie travels with
 *    every request.
 *  - Errors throw `AdminApiError` carrying `status` + `code` lifted from the
 *    `{ code, message }` envelope; `errorMessageFor(code)` maps known codes to
 *    Italian toast copy.
 *
 * IMPORTANT — wire format: the API serializes BigInt monetary fields as decimal
 * *strings* and `Date` fields as ISO strings (see `serializeBigInt` in
 * `apps/api/src/routes/admin/clienti.ts`). The view types below reflect that.
 */
import type { FidoAction, FidoDecision, UpdateFidoInput } from '@app/shared';

const API = '/api/v1/admin/clienti';

/* -------------------------------------------------------------------------- */
/* Wire types (post-serialization: cents = string, dates = ISO string)        */
/* -------------------------------------------------------------------------- */

export interface ClienteListItemView {
  id: string;
  email: string;
  name: string | null;
  role: string;
  tipologia: string | null;
  fidoLimitCents: string;
  balanceCents: string;
  /** disponibilità = saldo + fido. Può essere negativa (sconfinamento). */
  availableCents: string;
  gracePeriodUntil: string | null;
  walletBlocked: boolean;
  lastTopUpAt: string | null;
}

export interface ClienteListResponse {
  items: ClienteListItemView[];
  total: number;
  page: number;
  limit: number;
}

/** Serialized `Account` (monetary fields as strings, dates as ISO). */
export interface AccountView {
  id: string;
  email: string;
  name: string | null;
  role: string;
  tipologia: string | null;
  fidoLimitCents: string;
  gracePeriodUntil: string | null;
  walletBlocked: boolean;
  [key: string]: unknown;
}

export interface FidoAuditEntryView {
  id: string;
  accountId: string;
  adminId: string;
  oldValueCents: string;
  newValueCents: string;
  motivazione: string;
  action: FidoAction;
  ip: string | null;
  createdAt: string;
}

export interface ClienteDetailView {
  account: AccountView;
  balanceCents: string;
  availableCents: string;
  fidoAuditEntries: FidoAuditEntryView[];
}

export interface FidoMutationResponse {
  account: AccountView;
  entry: FidoAuditEntryView;
  action: FidoAction;
}

export interface ListClientiQuery {
  search?: string;
  page?: number;
  limit?: number;
}

/* -------------------------------------------------------------------------- */
/* Error handling                                                             */
/* -------------------------------------------------------------------------- */

export class AdminApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'AdminApiError';
    this.status = status;
    this.code = code;
  }
}

export function isAdminApiError(err: unknown): err is AdminApiError {
  return err instanceof AdminApiError;
}

/** Map API error `code` → Italian copy for toasts. */
const ERROR_MESSAGES: Record<string, string> = {
  RATE_LIMIT_EXCEEDED: "Troppi cambiamenti fido. Riprova tra un'ora.",
  VALIDATION_ERROR: 'Dati non validi. Controlla i campi.',
  FIDO_VALIDATION: 'Dati non validi. Controlla i campi.',
  FIDO_ACCOUNT_NOT_FOUND: 'Utente non trovato.',
  FORBIDDEN: 'Accesso negato.',
  UNAUTHORIZED: 'Sessione scaduta. Effettua di nuovo l\'accesso.',
};

/** Message shown when `fetch` itself fails (offline / DNS / CORS) — IMPL-P4-004. */
const NETWORK_ERROR_MESSAGE = 'Errore di connessione. Riprova.';

export function errorMessageFor(err: unknown): string {
  if (isAdminApiError(err)) {
    return ERROR_MESSAGES[err.code] ?? err.message ?? 'Si è verificato un errore.';
  }
  // A failed `fetch()` (no HTTP response: offline, DNS, CORS) rejects with a
  // TypeError before any AdminApiError is constructed — map it to a dedicated
  // connection-error copy rather than the generic fallback.
  if (err instanceof TypeError) {
    return NETWORK_ERROR_MESSAGE;
  }
  return 'Si è verificato un errore. Riprova.';
}

interface ApiErrorEnvelope {
  code?: string;
  message?: string;
}

async function parseErrorEnvelope(res: Response): Promise<ApiErrorEnvelope> {
  return (await res
    .json()
    .catch(() => ({ code: 'UNKNOWN', message: 'Errore sconosciuto' }))) as ApiErrorEnvelope;
}

async function throwForError(res: Response, fallbackCode: string, fallbackMsg: string): Promise<never> {
  const env = await parseErrorEnvelope(res);
  throw new AdminApiError(res.status, env.code ?? fallbackCode, env.message ?? fallbackMsg);
}

/* -------------------------------------------------------------------------- */
/* Endpoint wrappers                                                          */
/* -------------------------------------------------------------------------- */

/** GET /api/v1/admin/clienti — paginated business-account list. */
export async function listClienti(query: ListClientiQuery = {}): Promise<ClienteListResponse> {
  const params = new URLSearchParams();
  if (query.search) params.set('search', query.search);
  if (query.page !== undefined) params.set('page', String(query.page));
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  const qs = params.toString();
  const res = await fetch(`${API}${qs ? `?${qs}` : ''}`, { credentials: 'include' });
  if (!res.ok) await throwForError(res, 'LIST_FAILED', 'Impossibile caricare i clienti');
  return (await res.json()) as ClienteListResponse;
}

/** GET /api/v1/admin/clienti/:id — detail + audit trail. */
export async function getCliente(id: string): Promise<ClienteDetailView> {
  const res = await fetch(`${API}/${encodeURIComponent(id)}`, { credentials: 'include' });
  if (!res.ok) await throwForError(res, 'DETAIL_FAILED', 'Impossibile caricare il cliente');
  return (await res.json()) as ClienteDetailView;
}

/** PATCH /api/v1/admin/clienti/:id/fido — atomic fido update. */
export async function updateFido(
  id: string,
  body: { nuovoFidoEuro: number; motivazione: string; decision: FidoDecision },
): Promise<FidoMutationResponse> {
  const payload: UpdateFidoInput = body;
  const res = await fetch(`${API}/${encodeURIComponent(id)}/fido`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) await throwForError(res, 'FIDO_UPDATE_FAILED', 'Impossibile aggiornare il fido');
  return (await res.json()) as FidoMutationResponse;
}

/** PATCH /api/v1/admin/clienti/:id/wallet-toggle — invert walletBlocked. */
export async function toggleWallet(id: string): Promise<FidoMutationResponse> {
  const res = await fetch(`${API}/${encodeURIComponent(id)}/wallet-toggle`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) await throwForError(res, 'WALLET_TOGGLE_FAILED', 'Impossibile aggiornare lo stato del wallet');
  return (await res.json()) as FidoMutationResponse;
}
